import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import moment from 'moment';
// PrimeReact Components
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import { getInstitutionalFilter, getOrderById, updateOrderCart } from '../utilities/api';

const OrderEdit = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useRef(null);

    // States
    const [allData, setAllData] = useState({ company: [], category: [], products: [] });
    const [filteredCategories, setFilteredCategories] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [cartItems, setCartItems] = useState([]);
    const [remarks, setRemarks] = useState('');
    const [orderEditedBy, setOrderEditedBy] = useState('');
    const [deliveryDate, setDeliveryDate] = useState(null);
    const [deliveryTime, setDeliveryTime] = useState(null);
    const [sessionInfo, setSessionInfo] = useState({ ubranch: null, userid: null, branchid: null });
    const [loading, setLoading] = useState(false);
    const [orderDate, setOrderDate] = useState(null);
    const [orderTime, setOrderTime] = useState(null);

    // Initial Data Fetch (Filters)
    useEffect(() => {
        const fetchData = async () => {
            const bid = localStorage.getItem('branchid');
            setSessionInfo({ ubranch: localStorage.getItem('branch'), userid: localStorage.getItem('userid'), branchid: bid });
            try {
                const result = await getInstitutionalFilter({ branch_id: bid });
                setAllData(result || { company: [], category: [], products: [] });
            } catch (err) {
                console.error(err);
            }
        };
        fetchData();
    }, []);

    // Fetch Specific Order Data
    useEffect(() => {
        if (!id || !allData.company.length) return;
        const fetchOrder = async () => {
            try {
                const res = await getOrderById(id);
                const { header, items } = res;
                setRemarks(header.remarks || '');
                setOrderEditedBy(header.order_edited_by || '');
                setOrderDate(header.order_date ? new Date(header.order_date) : null);
                setOrderTime(header.order_time ? moment(header.order_time, 'hh:mm A').toDate() : null);
                setDeliveryDate(header.delivery_date ? new Date(header.delivery_date) : null);
                setDeliveryTime(header.delivery_time ? moment(header.delivery_time, 'hh:mm A').toDate() : null);

                const comp = allData.company.find((c) => c.company_id === header.company_id);
                if (comp) setSelectedCompany(comp);

                setCartItems(
                    items.map((i) => ({
                        id: i.spl_id,
                        name: i.product_name,
                        quantity: parseFloat(i.quantity) || 0, // Ensure decimal parsing
                        basic_rate: parseFloat(i.basic_rate),
                        tax: parseFloat(i.tax_percentage),
                        category_id: i.prcat_cat_id,
                        pr_id: i.pr_id,
                        isExisting: true,
                        billed_product_id: i.bill_pr_id
                    }))
                );
            } catch (err) {
                console.error(err);
            }
        };
        fetchOrder();
    }, [id, allData.company]);

    useEffect(() => {
        if (selectedCompany) setFilteredCategories(allData.category.filter((cat) => cat.company_id === selectedCompany.company_id));
    }, [selectedCompany, allData.category]);

    useEffect(() => {
        if (selectedCategory && selectedCompany) setFilteredProducts(allData.products.filter((p) => p.company_id === selectedCompany.company_id && p.prcat_cat_id === selectedCategory.code));
    }, [selectedCategory, selectedCompany, allData.products]);

    const addToCart = () => {
        if (!selectedProduct) return;
        const exists = cartItems.find((i) => i.id === selectedProduct.spl_id);
        if (exists) {
            setCartItems(cartItems.map((i) => (i.id === selectedProduct.spl_id ? { ...i, quantity: i.quantity + 1 } : i)));
        } else {
            setCartItems([
                ...cartItems,
                {
                    id: selectedProduct.spl_id,
                    name: selectedProduct.product_name,
                    basic_rate: parseFloat(selectedProduct.basic_rate),
                    tax: parseFloat(selectedProduct.basic_tax_percent),
                    quantity: 1.0, // Initial decimal value
                    isExisting: false,
                    pr_id: selectedProduct.pr_id,
                    category_id: selectedProduct.prcat_cat_id
                }
            ]);
        }
        setSelectedProduct(null);
    };

    const totalSummary = cartItems.reduce(
        (acc, i) => {
            const sub = (i.quantity || 0) * i.basic_rate;
            return { subtotal: acc.subtotal + sub, tax: acc.tax + (sub * i.tax) / 100 };
        },
        { subtotal: 0, tax: 0 }
    );

    const saveOrder = async () => {
        setLoading(true);
        const payload = {
            order_id: id,
            company_id: selectedCompany?.company_id,
            branch_id: sessionInfo.branchid,
            user_id: sessionInfo.userid,
            order_edited_by: orderEditedBy,
            remarks: remarks,
            order_date: orderDate ? moment(orderDate).format('YYYY-MM-DD') : null,
            order_time: orderTime ? moment(orderTime).format('hh:mm A') : null,
            delivery_date: deliveryDate ? moment(deliveryDate).format('YYYY-MM-DD') : null,
            delivery_time: deliveryTime ? moment(deliveryTime).format('hh:mm A') : null,
            subtotal: totalSummary.subtotal,
            total_tax_amount: totalSummary.tax,
            grand_total: totalSummary.subtotal + totalSummary.tax,
            items: cartItems.map((item) => {
                const lineSubtotal = item.quantity * item.basic_rate;
                return {
                    product_id: item.pr_id,
                    product_name: item.name,
                    spl_id: item.id,
                    quantity: item.quantity,
                    basic_rate: item.basic_rate,
                    tax_percentage: item.tax,
                    tax_amount: (lineSubtotal * item.tax) / 100,
                    category_id: item.category_id,
                    is_existing: item.isExisting,
                    billed_product_id: item.billed_product_id || null
                };
            })
        };

        try {
            const response = await updateOrderCart(payload);
            if (response.status === 'success') {
                toast.current.show({ severity: 'success', summary: 'Order Updated', detail: `Order #${id} has been saved successfully`, life: 3000 });
                setTimeout(() => navigate('/orderlist'), 2000);
            } else {
                throw new Error(response.message || 'Update failed');
            }
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Save Error', detail: err.message || 'Server error', life: 4000 });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-3 md:p-4 surface-200 min-h-screen font-sans text-900">
            <Toast ref={toast} />
            <ConfirmDialog />

            {/* TOP BAR */}
            <div className="flex align-items-center justify-content-between mb-3">
                <div className="flex align-items-center gap-3">
                    <Button icon="pi pi-arrow-left" className="p-button-rounded p-button-text p-button-plain surface-0 shadow-1" onClick={() => navigate('/orderlist')} />
                    <h5 className="m-0 font-bold text-2xl tracking-tight text-800">
                        Edit Order <span className="text-blue-600">#{id}</span>
                    </h5>
                </div>
                <div className="font-bold text-sm uppercase px-3 py-2 surface-0 border-round shadow-1 text-700">
                    <i className="pi pi-map-marker mr-2 text-primary"></i>
                    Branch: {sessionInfo.ubranch}
                </div>
            </div>

            {/* ROW 1: PRODUCT SELECTION & DATATABLE */}
            <Card className="mb-4 shadow-3 border-none p-2">
                <div className="p-fluid grid formgrid align-items-end mb-4 px-2">
                    <div className="field col-12 md:col-3">
                        <label className="text-sm font-bold text-700 uppercase mb-2 block">Customer Name</label>
                        <InputText value={selectedCompany?.company_name || ''} disabled className="p-inputtext-sm bg-gray-100 font-bold text-800 border-none" />
                    </div>
                    <div className="field col-12 md:col-3">
                        <label className="text-sm font-bold text-700 uppercase mb-2 block">Category Filter</label>
                        <Dropdown value={selectedCategory} options={filteredCategories} onChange={(e) => setSelectedCategory(e.value)} optionLabel="name" placeholder="Choose Category" className="p-inputtext-sm shadow-sm" filter />
                    </div>
                    <div className="field col-12 md:col-4">
                        <label className="text-sm font-bold text-700 uppercase mb-2 block">Select Product</label>
                        <Dropdown
                            value={selectedProduct}
                            options={filteredProducts}
                            onChange={(e) => setSelectedProduct(e.value)}
                            optionLabel="product_name"
                            placeholder="Search and Select..."
                            disabled={!selectedCategory}
                            className="p-inputtext-sm shadow-sm"
                            filter
                        />
                    </div>
                    <div className="field col-12 md:col-2">
                        <Button label="Add Item" icon="pi pi-plus" className="p-button-sm p-button-primary font-bold shadow-2" onClick={addToCart} disabled={!selectedProduct} />
                    </div>
                </div>

                <DataTable value={cartItems} className="p-datatable-sm mt-2" scrollable scrollHeight="400px" size="small" stripedRows responsiveLayout="scroll">
                    <Column field="name" header="PRODUCT DESCRIPTION" className="font-bold text-800" style={{ minWidth: '250px' }} />
                    <Column header="UNIT RATE" body={(r) => <span className="font-semibold">₹{r.basic_rate.toFixed(2)}</span>} />
                    <Column
                        header="QUANTITY"
                        body={(r) => (
                            <InputNumber
                                value={r.quantity}
                                onValueChange={(e) => setCartItems(cartItems.map((i) => (i.id === r.id ? { ...i, quantity: e.value } : i)))}
                                showButtons
                                min={0}
                                minFractionDigits={0}
                                maxFractionDigits={3} // Allows up to 3 decimal places
                                inputClassName="w-6rem p-inputtext-sm text-center font-bold"
                            />
                        )}
                    />
                    <Column header="TAX %" body={(r) => <span className="text-600 font-medium">{r.tax}%</span>} />
                    <Column header="TOTAL AMOUNT" className="text-right" body={(r) => <span className="font-bold text-blue-700">₹{(r.basic_rate * (r.quantity || 0) * (1 + r.tax / 100)).toFixed(2)}</span>} />
                    <Column style={{ width: '4rem' }} body={(r) => <Button icon="pi pi-trash" className="p-button-danger p-button-text p-button-rounded" onClick={() => setCartItems(cartItems.filter((i) => i.id !== r.id))} />} />
                </DataTable>
            </Card>

            {/* ROW 2: BOTTOM INFO PANELS */}
            <div className="grid grid-nogutter align-items-stretch">
                <div className="col-12 lg:col-8 pr-0 lg:pr-4">
                    <Card className="shadow-3 border-none p-3 h-full">
                        <div className="flex align-items-center mb-4 text-primary border-bottom-1 pb-2 border-200">
                            <i className="pi pi-truck mr-2 font-bold text-xl"></i>
                            <h6 className="m-0 text-sm font-bold uppercase tracking-wider">Order & Delivery Details</h6>
                        </div>

                        <div className="p-fluid grid formgrid">
                            <div className="field col-12 md:col-6 px-2">
                                <label className="text-xs font-bold text-700 uppercase mb-2 block">Order Date</label>
                                <Calendar value={orderDate} onChange={(e) => setOrderDate(e.value)} dateFormat="dd/mm/yy" showIcon className="p-inputtext-sm" />
                            </div>
                            <div className="field col-12 md:col-6 px-2 mb-4">
                                <label className="text-xs font-bold text-700 uppercase mb-2 block">Order Time</label>
                                <Calendar value={orderTime} onChange={(e) => setOrderTime(e.value)} timeOnly hourFormat="12" showIcon className="p-inputtext-sm" />
                            </div>

                            <div className="field col-12 md:col-6 px-2">
                                <label className="text-xs font-bold text-700 uppercase mb-2 block">Exp. Delivery Date</label>
                                <Calendar value={deliveryDate} onChange={(e) => setDeliveryDate(e.value)} dateFormat="dd/mm/yy" showIcon className="p-inputtext-sm" />
                            </div>
                            <div className="field col-12 md:col-6 px-2 mb-4">
                                <label className="text-xs font-bold text-700 uppercase mb-2 block">Exp. Delivery Time</label>
                                <Calendar value={deliveryTime} onChange={(e) => setDeliveryTime(e.value)} timeOnly hourFormat="12" showIcon className="p-inputtext-sm" />
                            </div>

                            <div className="field col-12 md:col-6 px-2">
                                <label className="text-xs font-bold text-700 uppercase mb-2 block">Order Edited By</label>
                                <InputText value={orderEditedBy} onChange={(e) => setOrderEditedBy(e.target.value)} placeholder="User Name" className="p-inputtext-sm" />
                            </div>
                            <div className="field col-12 md:col-6 px-2">
                                <label className="text-xs font-bold text-700 uppercase mb-2 block">Special Remarks</label>
                                <InputText value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Instructions..." className="p-inputtext-sm" />
                            </div>
                        </div>
                    </Card>
                </div>

                <div className="col-12 lg:col-4 mt-4 lg:mt-0">
                    <Card className="shadow-3 border-none p-4 bg-white h-full border-top-3 border-primary flex flex-column">
                        <div className="flex align-items-center mb-4 text-700 border-bottom-1 pb-2 border-200">
                            <i className="pi pi-receipt mr-2 font-bold text-xl"></i>
                            <h6 className="m-0 text-sm font-bold uppercase tracking-wider">Billing Summary</h6>
                        </div>

                        <div className="flex justify-content-between mb-2">
                            <span className="text-700 font-semibold">Subtotal:</span>
                            <span className="font-bold text-800">₹{totalSummary.subtotal.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-content-between mb-3">
                            <span className="text-700 font-semibold">Total Tax:</span>
                            <span className="text-orange-600 font-bold">+ ₹{totalSummary.tax.toFixed(2)}</span>
                        </div>

                        <Divider className="my-3" />

                        <div className="flex justify-content-between align-items-center mb-5">
                            <span className="text-lg font-bold text-900">GRAND TOTAL:</span>
                            <span className="text-3xl font-black text-primary">₹{(totalSummary.subtotal + totalSummary.tax).toFixed(2)}</span>
                        </div>

                        <div className="mt-auto">
                            <Button
                                label="SUBMIT ORDER UPDATES"
                                icon="pi pi-save"
                                loading={loading}
                                className="p-button-success w-full py-3 font-bold shadow-2 text-lg"
                                onClick={() =>
                                    confirmDialog({
                                        message: 'Save changes to this order?',
                                        header: 'Confirmation',
                                        accept: saveOrder
                                    })
                                }
                                disabled={!cartItems.length || !orderEditedBy || loading}
                            />
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default OrderEdit;
